import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Edit2, XCircle, FileText, Calendar, AlertTriangle, Layers, Trash2, ChevronRight, ChevronDown, CircleDot, CircleDashed, CheckCircle2, Link2 } from 'lucide-react';
import { formatCurrency, formatDate, getStatusBadge, getUtilizationColor } from '../../utils/poFormatters.jsx';
import { scrollAndFlash } from '../../lib/highlight';
import { TablePagination, PAGE_SIZE } from '../shared/TablePagination';
import { ColumnFilterPopover } from '../shared/ColumnFilterPopover';
import { renderCustomValue } from '../shared/EntityCustomFields';

const WorkProgressBadge = ({ po }) => {
  const progress = po.work_progress || 'not_started';
  if (progress === 'completed') {
    return (
      <Badge className="bg-green-100 text-green-700 border-green-300 gap-1" data-testid={`po-progress-${po.id}`}>
        <CheckCircle2 className="w-3 h-3" /> Completed
      </Badge>
    );
  }
  if (progress === 'in_progress') {
    return (
      <Badge className="bg-blue-100 text-blue-700 border-blue-300 gap-1" data-testid={`po-progress-${po.id}`}>
        <CircleDot className="w-3 h-3" /> In Progress
      </Badge>
    );
  }
  // not_started
  if (po.work_not_started_overdue) {
    return (
      <Badge className="bg-red-100 text-red-700 border-red-300 gap-1" data-testid={`po-progress-${po.id}`} title="No work tagged within 5 days of receipt">
        <AlertTriangle className="w-3 h-3" /> Not Started &gt;5d
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground gap-1" data-testid={`po-progress-${po.id}`}>
      <CircleDashed className="w-3 h-3" /> Not Started
    </Badge>
  );
};

const LineItemsDetail = ({ po, colSpan, onToggleLineItem }) => {
  const items = po.line_items || [];
  return (
    <TableRow className="bg-muted/30 hover:bg-muted/30" data-testid={`po-line-items-detail-${po.id}`}>
      <TableCell colSpan={colSpan} className="py-3">
        <div className="pl-14 pr-4">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Line Items — {po.po_number}
          </div>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No line items recorded for this PO.</p>
          ) : (
            <div className="rounded-md border bg-white overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="h-9">#</TableHead>
                    <TableHead className="h-9">Description</TableHead>
                    <TableHead className="h-9 text-right">Qty</TableHead>
                    <TableHead className="h-9 text-right">Unit Price</TableHead>
                    <TableHead className="h-9 text-right">Amount</TableHead>
                    <TableHead className="h-9 text-center">Delivered</TableHead>
                    <TableHead className="h-9 text-center">Invoiced</TableHead>
                    <TableHead className="h-9">Delivery Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((li, idx) => (
                    <TableRow key={li.id || idx} data-testid={`po-line-item-row-${po.id}-${idx}`}>
                      <TableCell className="text-xs text-muted-foreground">{idx + 1}</TableCell>
                      <TableCell className="text-sm">{li.description || '—'}{li.notes && <span className="block text-xs text-muted-foreground">{li.notes}</span>}</TableCell>
                      <TableCell className="text-sm text-right">{li.quantity ?? '—'}</TableCell>
                      <TableCell className="text-sm text-right">{li.unit_price != null ? formatCurrency(li.unit_price, po.currency) : '—'}</TableCell>
                      <TableCell className="text-sm text-right font-medium">{formatCurrency(li.amount || 0, po.currency)}</TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={!!li.delivered}
                          onCheckedChange={(v) => onToggleLineItem && onToggleLineItem(po, idx, 'delivered', !!v)}
                          data-testid={`po-line-item-delivered-${po.id}-${idx}`}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={!!li.invoiced}
                          onCheckedChange={(v) => onToggleLineItem && onToggleLineItem(po, idx, 'invoiced', !!v)}
                          data-testid={`po-line-item-invoiced-${po.id}-${idx}`}
                        />
                      </TableCell>
                      <TableCell className="text-sm">{li.delivery_date ? formatDate(li.delivery_date) : <span className="text-muted-foreground">—</span>}
                        {li.overdue && <Badge className="ml-2 bg-red-100 text-red-700 border-red-300 text-[10px] px-1.5 py-0" data-testid={`po-line-item-overdue-${po.id}-${idx}`}>Overdue</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export const POTable = ({
  pos, loading, search, filterStatus, onNewPo, onEdit, onAddExtension, onCancel, onViewExtensions,
  selectedIds, onToggleSelect, onToggleSelectAll, onDeleteSingle, workloadTypeOptions = [],
  colFilters, setColFilter, statusOpts = [], workloadOpts = [], msOwnerOpts = [], progressOpts = [], customFields = [],
  isSuperAdmin = false, onToggleLineItem,
}) => {
  const workloadLabel = (value) => workloadTypeOptions.find(o => o.value === value)?.label || value;
  const [page, setPage] = React.useState(1);
  const [expandedId, setExpandedId] = React.useState(null);
  React.useEffect(() => { setPage(1); }, [search, filterStatus]);
  const totalRows = pos.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedPos = pos.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const colCount = 9 + customFields.length; // checkbox, PO, MS Owner, Total, Utilization, Progress, Line Items, Validity, Status, [custom], Actions
  const totalColSpan = colCount + 1;
  return (
  <Card>
    <CardContent className="pt-6">
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded" />)}
        </div>
      ) : pos.length > 0 ? (
        <Table containerClassName="max-h-[calc(100vh-320px)]">
          <TableHeader sticky>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={pos.filter(po => po._po_type !== 'content').length > 0 && pos.filter(po => po._po_type !== 'content').every(po => selectedIds.has(po.id))}
                  onCheckedChange={(checked) => onToggleSelectAll(checked, pos.filter(po => po._po_type !== 'content').map(po => po.id))}
                  data-testid="select-all-pos-checkbox"
                />
              </TableHead>
              <TableHead>
                <div className="flex items-center gap-1">PO Number
                  {setColFilter && <ColumnFilterPopover label="Workload" options={workloadOpts} selected={colFilters.workload} onChange={(s) => setColFilter('workload', s)} testId="po-col-filter-workload" />}
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center gap-1">MS Owner
                  {setColFilter && <ColumnFilterPopover label="MS Owner" options={msOwnerOpts} selected={colFilters.ms_owner} onChange={(s) => setColFilter('ms_owner', s)} testId="po-col-filter-ms-owner" />}
                </div>
              </TableHead>
              <TableHead>Total Value</TableHead>
              <TableHead>Utilization</TableHead>
              <TableHead>
                <div className="flex items-center gap-1">Work Progress
                  {setColFilter && <ColumnFilterPopover label="Progress" options={progressOpts} selected={colFilters.progress} onChange={(s) => setColFilter('progress', s)} testId="po-col-filter-progress" />}
                </div>
              </TableHead>
              <TableHead>Line Items</TableHead>
              <TableHead>Validity</TableHead>
              <TableHead>{setColFilter ? <ColumnFilterPopover label="Status" options={statusOpts} selected={colFilters.status} onChange={(s) => setColFilter('status', s)} testId="po-col-filter-status" /> : 'Status'}</TableHead>
              {customFields.map(f => <TableHead key={f.id}>{f.label}</TableHead>)}
              <TableHead className="w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedPos.map((po) => {
              const pct = po.utilization_percentage || 0;
              const util = getUtilizationColor(pct);
              const isAtRisk = po.status === 'active' && pct >= 80;
              const hasUsage = (po.used_amount || 0) > 0;
              const liTotal = po.line_items_count != null ? po.line_items_count : (po.line_items || []).length;
              const liDelivered = po.line_items_delivered || 0;
              const liInvoiced = po.line_items_invoiced || 0;
              const liInvPct = liTotal > 0 ? Math.round((liInvoiced / liTotal) * 100) : 0;
              const isExpanded = expandedId === po.id;
              const expiringSoon = po.validity_expiring_soon;
              const rowHighlight = po.work_not_started_overdue ? 'bg-red-50' : (isAtRisk ? util.bg : (expiringSoon ? 'bg-amber-50' : ''));
              return (
                <React.Fragment key={po.id}>
                <TableRow data-testid={`po-row-${po.id}`} className={rowHighlight}>
                  <TableCell>
                    {po._po_type !== 'content' && (
                      <Checkbox
                        checked={selectedIds.has(po.id)}
                        onCheckedChange={() => onToggleSelect(po.id)}
                        data-testid={`select-po-${po.id}`}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${po.work_not_started_overdue ? 'bg-red-100' : (isAtRisk ? 'bg-amber-100' : 'bg-primary/10')}`}>
                        {po.work_not_started_overdue ? <AlertTriangle className="w-5 h-5 text-red-600" /> : isAtRisk ? <AlertTriangle className="w-5 h-5 text-amber-600" /> : <FileText className="w-5 h-5 text-primary" />}
                      </div>
                      <div>
                        <div className="font-medium flex items-center gap-1.5">
                          {po.po_number}
                          <Badge
                            variant="outline"
                            className={`text-[10px] px-1.5 py-0 font-normal ${po._po_type === 'content' ? 'border-purple-300 text-purple-700 bg-purple-50' : 'border-blue-300 text-blue-700 bg-blue-50'}`}
                            data-testid={`po-type-badge-${po.id}`}
                          >
                            {po.workload_type ? workloadLabel(po.workload_type) : (po._po_type === 'content' ? 'Content' : 'Delivery')}
                          </Badge>
                          {po.po_type === 'extension' && (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1.5 py-0 font-normal border-amber-300 text-amber-700 bg-amber-50"
                              data-testid={`po-extension-badge-${po.id}`}
                            >
                              Extension
                            </Badge>
                          )}
                        </div>
                        {po.short_title && <div className="text-xs text-muted-foreground">{po.short_title}</div>}
                        {po.po_type === 'extension' && po.parent_po_number && (
                          <button
                            type="button"
                            onClick={() => scrollAndFlash(`po-row-${po.parent_po_id}`)}
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                            data-testid={`po-extension-parent-${po.id}`}
                          >
                            <Link2 className="w-3 h-3" /> Parent PO: {po.parent_po_number}
                          </button>
                        )}
                        {po.sow_reference && <div className="text-xs text-muted-foreground">SOW: {po.sow_reference}</div>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm" data-testid={`po-ms-owner-${po.id}`}>{po.ms_owner || <span className="text-muted-foreground">—</span>}</span>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{formatCurrency(po.consolidated_total_amount ?? po.total_amount, po.currency)}</div>
                      {po.extensions_count > 0 ? (
                        <button
                          type="button"
                          onClick={() => onViewExtensions(po)}
                          className="text-xs text-blue-600 hover:underline"
                          data-testid={`view-extensions-${po.id}`}
                        >
                          Base {formatCurrency(po.total_amount, po.currency)} + {po.extensions_count} extension{po.extensions_count > 1 ? 's' : ''}
                        </button>
                      ) : (
                        <div className="text-xs text-muted-foreground">Available: {formatCurrency(po.available_balance, po.currency)}</div>
                      )}
                      {po.extensions_count > 0 && (
                        <div className="text-xs text-muted-foreground">Available: {formatCurrency(po.available_balance, po.currency)}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1 min-w-36">
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${util.bar}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                      </div>
                      <div className={`text-xs font-medium ${util.text}`}>
                        {pct}% used — {formatCurrency(po.used_amount || 0, po.currency)} of {formatCurrency(po.consolidated_total_amount ?? po.total_amount, po.currency)}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <WorkProgressBadge po={po} />
                  </TableCell>
                  <TableCell>
                    {liTotal > 0 ? (
                      <div className="space-y-1 min-w-28">
                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : po.id)}
                          className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                          data-testid={`po-line-items-toggle-${po.id}`}
                        >
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          {liTotal} item{liTotal > 1 ? 's' : ''}
                        </button>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${liInvPct >= 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${liInvPct}%` }} />
                        </div>
                        <div className="text-[11px] text-muted-foreground" data-testid={`po-line-items-rollup-${po.id}`}>
                          {liDelivered}/{liTotal} delivered · {liInvoiced}/{liTotal} invoiced
                          {(po.line_items_overdue || 0) > 0 && (
                            <span className="text-red-600 font-medium"> · {po.line_items_overdue} overdue</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground" data-testid={`po-line-items-toggle-${po.id}`}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm" data-testid={`po-validity-${po.id}`}>
                      <Calendar className={`w-3 h-3 ${expiringSoon ? 'text-amber-600' : 'text-muted-foreground'}`} />
                      {po.validity_date ? formatDate(po.validity_date) : <span className="text-muted-foreground">—</span>}
                    </div>
                    {expiringSoon && (
                      <Badge className="mt-1 bg-amber-100 text-amber-800 border-amber-300 text-[10px] px-1.5 py-0" data-testid={`po-validity-expiring-${po.id}`}>
                        {po.validity_days_left === 0 ? 'Expires today' : `Expires in ${po.validity_days_left}d`}
                      </Badge>
                    )}
                    <div className="text-xs text-muted-foreground">Issued: {formatDate(po.issue_date)}</div>
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(po.status, po.validity_date || po.expiry_date)}
                    {isAtRisk && pct < 100 && <Badge className="ml-1 bg-amber-100 text-amber-800 border-amber-300 text-xs">At Risk</Badge>}
                  </TableCell>
                  {customFields.map(f => (
                    <TableCell key={f.id} className="text-sm">{renderCustomValue(po.custom_fields?.[f.taxonomy_id], f.options) || <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                  ))}
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => onEdit(po)} data-testid={`edit-po-${po.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {po.status !== 'cancelled' && po.status !== 'closed' && po.po_type !== 'extension' && (
                        <Button variant="ghost" size="icon" onClick={() => onAddExtension(po)} title="Add Extension PO" data-testid={`add-extension-${po.id}`}>
                          <Layers className="w-4 h-4" />
                        </Button>
                      )}
                      {(isSuperAdmin || !hasUsage) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDeleteSingle(po)}
                          className="text-destructive hover:text-destructive"
                          title={hasUsage ? (isSuperAdmin ? 'Has billed usage — Super Admin can still delete' : 'Has billed usage — cancel instead') : 'Permanently delete this PO'}
                          data-testid={`delete-po-${po.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                {isExpanded && <LineItemsDetail po={po} colSpan={totalColSpan} onToggleLineItem={onToggleLineItem} />}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No Purchase Orders found</h3>
          <p className="text-muted-foreground mb-4">
            {search || filterStatus !== 'all' ? 'Try adjusting your filters' : 'Create your first blanket PO to get started'}
          </p>
          {!search && filterStatus === 'all' && (
            <Button onClick={onNewPo}><Plus className="w-4 h-4 mr-2" />New PO</Button>
          )}
        </div>
      )}
      <TablePagination page={safePage} total={totalRows} onPageChange={setPage} testId="po-pagination" itemLabel="POs" />
    </CardContent>
  </Card>
  );
};

export default POTable;
